import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  FileText,
  Download,
  ShieldCheck,
  CheckCircle2,
  Clock,
  XCircle,
  Wallet,
  Pencil,
  Loader2,
} from "lucide-react";

import { toast } from "sonner";
import {
  adminGetDocumentUrl,
  adminListDocuments,
  adminUpdateDocumentCase,
} from "@/lib/api/documents.functions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/portal/admin/documents")({
  head: () => ({
    meta: [
      { title: "Client cases & documents (admin) | Portal" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminDocumentsPage,
});

type Doc = {
  id: string;
  original_filename: string;
  uploader_name: string;
  uploader_email: string;
  uploader_phone: string | null;
  matter_summary: string | null;
  storage_path: string;
  size_bytes: number;
  created_at: string;
  case_reference: string | null;
  case_status: "new" | "in_review" | "in_progress" | "awaiting_client" | "closed";
  payment_status: "unpaid" | "partial" | "paid" | "waived";
  amount_due_cents: number;
  amount_paid_cents: number;
  admin_notes: string | null;
};

function bytesPretty(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
function rand(cents: number) {
  return `R ${(cents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;
}

const CASE_LABELS: Record<Doc["case_status"], string> = {
  new: "New",
  in_review: "In review",
  in_progress: "In progress",
  awaiting_client: "Awaiting client",
  closed: "Closed",
};

const PAY_LABELS: Record<Doc["payment_status"], string> = {
  unpaid: "Unpaid",
  partial: "Partial",
  paid: "Paid",
  waived: "Waived",
};

function PayBadge({ s }: { s: Doc["payment_status"] }) {
  const map = {
    paid: { c: "bg-emerald-100 text-emerald-700 border-emerald-200", I: CheckCircle2 },
    partial: { c: "bg-amber-100 text-amber-700 border-amber-200", I: Clock },
    unpaid: { c: "bg-rose-100 text-rose-700 border-rose-200", I: XCircle },
    waived: { c: "bg-slate-100 text-slate-700 border-slate-200", I: Wallet },
  } as const;
  const { c, I } = map[s];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${c}`}
    >
      <I className="h-3 w-3" /> {PAY_LABELS[s]}
    </span>
  );
}

function CaseBadge({ s }: { s: Doc["case_status"] }) {
  const c =
    s === "closed"
      ? "bg-slate-100 text-slate-600 border-slate-200"
      : s === "in_progress"
        ? "bg-sky-100 text-sky-700 border-sky-200"
        : s === "awaiting_client"
          ? "bg-amber-100 text-amber-700 border-amber-200"
          : s === "in_review"
            ? "bg-indigo-100 text-indigo-700 border-indigo-200"
            : "bg-navy/10 text-navy border-navy/20";
  return (
    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${c}`}>
      {CASE_LABELS[s]}
    </span>
  );
}

function AdminDocumentsPage() {
  const qc = useQueryClient();
  const fetchAll = useServerFn(adminListDocuments);
  const signUrl = useServerFn(adminGetDocumentUrl);
  const updateCase = useServerFn(adminUpdateDocumentCase);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-documents"],
    queryFn: () => fetchAll(),
  });

  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<Doc | null>(null);
  const [approving, setApproving] = useState<Set<string>>(new Set());

  async function approveDoc(id: string) {
    setApproving((prev) => new Set(prev).add(id));
    try {
      await updateCase({ data: { id, case_status: "in_progress" } });
      toast.success("Document approved — case moved to In progress");
      qc.invalidateQueries({ queryKey: ["admin-documents"] });
    } catch (e: any) {
      toast.error("Approval failed", { description: e?.message });
    } finally {
      setApproving((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  async function open(path: string) {
    try {
      const res = await signUrl({ data: { storage_path: path } });
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      toast.error("Could not open file", { description: e?.message });
    }
  }

  const docs = (data?.documents ?? []) as Doc[];

  const cases = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? docs.filter(
          (d) =>
            (d.uploader_name ?? "").toLowerCase().includes(q) ||
            (d.uploader_email ?? "").toLowerCase().includes(q) ||
            (d.case_reference ?? "").toLowerCase().includes(q) ||
            (d.matter_summary ?? "").toLowerCase().includes(q),
        )
      : docs;
    const groups = new Map<string, Doc[]>();
    for (const d of filtered) {
      const key = (d.uploader_email || d.uploader_name || d.id).toLowerCase();
      const arr = groups.get(key) ?? [];
      arr.push(d);
      groups.set(key, arr);
    }
    return Array.from(groups.values()).sort(
      (a, b) =>
        new Date(b[0].created_at).getTime() - new Date(a[0].created_at).getTime(),
    );
  }, [docs, query]);

  const totals = useMemo(() => {
    const due = docs.reduce((s, d) => s + (d.amount_due_cents ?? 0), 0);
    const paid = docs.reduce((s, d) => s + (d.amount_paid_cents ?? 0), 0);
    const open = docs.filter((d) => d.case_status !== "closed").length;
    return { due, paid, open };
  }, [docs]);

  return (
    <div className="space-y-6">
      <header className="flex items-start gap-3">
        <ShieldCheck className="mt-1 h-5 w-5 text-gold" />
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
          <h2 className="font-display text-2xl text-navy">Client cases</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Every upload becomes a case. Track payment status, add notes, and
            open files with secure 10-minute links.
          </p>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Open cases</p>
          <p className="mt-1 font-display text-2xl text-navy">{totals.open}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Billed</p>
          <p className="mt-1 font-display text-2xl text-navy">{rand(totals.due)}</p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Collected</p>
          <p className="mt-1 font-display text-2xl text-emerald-600">{rand(totals.paid)}</p>
        </div>
      </div>

      <Input
        placeholder="Search by client name, email, case ref, or matter…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : cases.length === 0 ? (
        <p className="text-sm text-muted-foreground">No uploads yet.</p>
      ) : (
        <div className="space-y-4">
          {cases.map((group) => {
            const head = group[0];
            return (
              <section
                key={head.uploader_email + head.uploader_name}
                className="rounded-xl border border-border bg-card"
              >
                <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-navy">
                      {head.uploader_name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {head.uploader_email}
                      {head.uploader_phone ? ` · ${head.uploader_phone}` : ""}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {group.length} file{group.length > 1 ? "s" : ""}
                  </p>
                </header>
                <ul className="divide-y divide-border">
                  {group.map((d) => (
                    <li
                      key={d.id}
                      className="flex flex-wrap items-start gap-3 p-4"
                    >
                      <FileText className="mt-0.5 h-4 w-4 text-navy" />
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-mono text-navy">
                            {d.case_reference ?? "—"}
                          </span>
                          <CaseBadge s={d.case_status} />
                          <PayBadge s={d.payment_status} />
                          {d.amount_due_cents > 0 && (
                            <span className="text-[11px] text-muted-foreground">
                              {rand(d.amount_paid_cents)} / {rand(d.amount_due_cents)}
                            </span>
                          )}
                        </div>
                        <p className="truncate text-sm font-medium text-navy">
                          {d.original_filename}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {bytesPretty(d.size_bytes)} ·{" "}
                          {new Date(d.created_at).toLocaleString("en-ZA")}
                        </p>
                        {d.matter_summary && (
                          <p className="text-xs text-muted-foreground">
                            {d.matter_summary}
                          </p>
                        )}
                        {d.admin_notes && (
                          <p className="rounded bg-secondary/40 p-2 text-xs text-foreground">
                            📝 {d.admin_notes}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => open(d.storage_path)}
                          className="inline-flex items-center gap-2 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
                        >
                          <Download className="h-3.5 w-3.5" /> Open
                        </button>
                        <button
                          onClick={() => setEditing(d)}
                          className="inline-flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium hover:border-navy"
                        >
                          <Pencil className="h-3.5 w-3.5" /> Manage
                        </button>
                        {(d.case_status === "new" || d.case_status === "in_review") && (
                          <button
                            onClick={() => approveDoc(d.id)}
                            disabled={approving.has(d.id)}
                            className="inline-flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                          >
                            {approving.has(d.id) ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            )}
                            Approve
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <EditDialog
        doc={editing}
        onClose={() => setEditing(null)}
        onSave={async (patch) => {
          try {
            await updateCase({ data: patch });
            toast.success("Case updated");
            setEditing(null);
            qc.invalidateQueries({ queryKey: ["admin-documents"] });
          } catch (e: any) {
            toast.error("Update failed", { description: e?.message });
          }
        }}
      />
    </div>
  );
}

function EditDialog({
  doc,
  onClose,
  onSave,
}: {
  doc: Doc | null;
  onClose: () => void;
  onSave: (patch: {
    id: string;
    case_status: Doc["case_status"];
    payment_status: Doc["payment_status"];
    amount_due_cents: number;
    amount_paid_cents: number;
    admin_notes: string;
  }) => void;
}) {
  const [caseStatus, setCaseStatus] = useState<Doc["case_status"]>("new");
  const [payStatus, setPayStatus] = useState<Doc["payment_status"]>("unpaid");
  const [due, setDue] = useState("0");
  const [paid, setPaid] = useState("0");
  const [notes, setNotes] = useState("");

  // Reset whenever the dialog opens with a new doc
  useMemo(() => {
    if (doc) {
      setCaseStatus(doc.case_status);
      setPayStatus(doc.payment_status);
      setDue(((doc.amount_due_cents ?? 0) / 100).toString());
      setPaid(((doc.amount_paid_cents ?? 0) / 100).toString());
      setNotes(doc.admin_notes ?? "");
    }
  }, [doc]);

  if (!doc) return null;

  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{doc.case_reference}</DialogTitle>
          <DialogDescription>
            {doc.uploader_name} · {doc.uploader_email}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Case status</Label>
            <Select value={caseStatus} onValueChange={(v) => setCaseStatus(v as any)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CASE_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment status</Label>
            <Select value={payStatus} onValueChange={(v) => setPayStatus(v as any)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PAY_LABELS).map(([k, l]) => (
                  <SelectItem key={k} value={k}>
                    {l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount due (R)</Label>
            <Input
              inputMode="decimal"
              value={due}
              onChange={(e) => setDue(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Amount paid (R)</Label>
            <Input
              inputMode="decimal"
              value={paid}
              onChange={(e) => setPaid(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Internal notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={4000}
              className="mt-1 min-h-[96px]"
              placeholder="Visible to admins only."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {doc.case_status !== "in_progress" && (
            <Button
              variant="outline"
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              onClick={() =>
                onSave({
                  id: doc.id,
                  case_status: "in_progress",
                  payment_status: payStatus,
                  amount_due_cents: Math.round((parseFloat(due) || 0) * 100),
                  amount_paid_cents: Math.round((parseFloat(paid) || 0) * 100),
                  admin_notes: notes,
                })
              }
            >
              <CheckCircle2 className="mr-2 h-4 w-4" /> Approve
            </Button>
          )}
          <Button
            className="bg-navy hover:bg-navy/90"
            onClick={() =>
              onSave({
                id: doc.id,
                case_status: caseStatus,
                payment_status: payStatus,
                amount_due_cents: Math.round((parseFloat(due) || 0) * 100),
                amount_paid_cents: Math.round((parseFloat(paid) || 0) * 100),
                admin_notes: notes,
              })
            }
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
